/**
 * Column Width Utilities for FormCrafter
 * Functions for calculating and applying column widths dynamically
 */

// Gap size used in the fields container (should match CSS)
const FIELD_GAP = 16;

/**
 * Calculates CSS width from column width value, accounting for container gap
 * Handles fractions (1/2, 1/3, 2/3), whole numbers, and CSS values
 *
 * @param {string|number} columnWidth - Column width value (e.g., "1", "1/2", "1/3", "50%")
 * @param {number} gap - Gap size in pixels to account for (default: 16)
 * @returns {string} - CSS calc() width value that accounts for gaps
 */
export const getColumnWidth = (columnWidth, gap = FIELD_GAP) => {
  if (!columnWidth) return '100%';

  const value = String(columnWidth).trim();

  // Handle whole number "1" as full width
  if (value === '1') return '100%';

  // Handle fractions like "1/2", "1/3", "2/3", "1/4", "3/4"
  if (value.includes('/')) {
    const [numerator, denominator] = value.split('/').map(Number);
    if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
      const percentage = (numerator / denominator) * 100;
      // Calculate gap adjustment: for N columns, there are (N-1) gaps
      // Each column needs to subtract (gap * (N-1) / N) pixels
      const gapAdjustment = (gap * (denominator - 1)) / denominator;
      return `calc(${percentage.toFixed(2)}% - ${gapAdjustment.toFixed(0)}px)`;
    }
  }

  // Handle numeric values (treat as percentage)
  const numValue = parseFloat(value);
  if (!isNaN(numValue)) {
    // If it's a decimal less than 1, treat as fraction
    if (numValue > 0 && numValue < 1) {
      const percentage = numValue * 100;
      // Estimate number of columns based on fraction
      const estimatedCols = Math.round(1 / numValue);
      const gapAdjustment = (gap * (estimatedCols - 1)) / estimatedCols;
      return `calc(${percentage.toFixed(2)}% - ${gapAdjustment.toFixed(0)}px)`;
    }
    // If it's already a percentage-like number
    if (numValue > 1 && numValue <= 100) {
      return `${numValue}%`;
    }
  }

  // If it already has CSS units, return as-is
  if (
    value.includes('%') ||
    value.includes('px') ||
    value.includes('em') ||
    value.includes('rem') ||
    value.includes('calc')
  ) {
    return value;
  }

  // Default to full width
  return '100%';
};

/**
 * Creates a style object for column width
 *
 * @param {string|number} columnWidth - Column width value
 * @returns {Object} - Style object with flex properties
 */
export const getColumnStyle = (columnWidth) => {
  const width = getColumnWidth(columnWidth);
  return {
    flex: `0 0 ${width}`,
    maxWidth: width,
    boxSizing: 'border-box',
  };
};
